import React, { useEffect, useState } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { supabase } from '@/integrations/supabase/client';
import { motion } from 'framer-motion';
import { Save, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';

const WebsiteContent = () => {
  const [content, setContent] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const defaultContent = [
    { key: 'hero_headline', section: 'Hero', content: 'Relax at Walnut Farm' },
    { key: 'hero_description', section: 'Hero', content: 'Experience a serene farmhouse getaway with a pool and open kitchen for unforgettable memories.' },
    { key: 'hero_cta', section: 'Hero', content: 'Enquire on WhatsApp' },
    { key: 'intro_eyebrow', section: 'Intro', content: 'Welcome to Walnut Farm Retreat' },
    { key: 'intro_left_headline', section: 'Intro', content: 'Your Perfect Getaway' },
    { key: 'intro_left_description', section: 'Intro', content: 'At Walnut Farm, we offer a serene farmhouse experience with three cozy rooms, a refreshing pool, and an open kitchen, perfect for your next getaway or family gathering.' },
    { key: 'exp_eyebrow', section: 'Experience', content: 'PRIVATE FARMHOUSE EXPERIENCE' },
    { key: 'exp_headline', section: 'Experience', content: 'Relaxing Farmhouse Retreat' },
    { key: 'exp_description', section: 'Experience', content: 'Experience a serene getaway in our farmhouse with three cozy rooms and a refreshing pool.' },
    { key: 'footer_about', section: 'Footer', content: 'A meticulously designed private farmhouse retreat where modern luxury meets the timeless tranquility of nature.' }
  ];

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const { data, error } = await supabase.from('page_content').select('*');
      if (error) throw error;
      
      // Merge with defaults to ensure all keys exist
      const merged = defaultContent.map(def => {
        const existing = data?.find(d => d.key === def.key);
        return existing || { ...def, isNew: true };
      });
      
      setContent(merged);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key: string, value: string) => {
    setContent(prev => prev.map(item => item.key === key ? { ...item, content: value } : item));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      for (const item of content) {
        const { id, isNew, ...saveItem } = item;
        if (isNew) {
          const { error } = await supabase.from('page_content').insert([saveItem]);
          if (error) throw error;
        } else {
          const { error } = await supabase.from('page_content').update({ content: item.content }).eq('key', item.key);
          if (error) throw error;
        }
      }
      setMessage({ type: 'success', text: 'Website content updated successfully!' });
      fetchContent();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to save content' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <AdminLayout><div className="flex items-center justify-center h-full"><Loader2 className="animate-spin text-emerald" /></div></AdminLayout>;

  const sections = Array.from(new Set(content.map(c => c.section)));

  return (
    <AdminLayout>
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-serif text-emerald">Website Content</h1>
            <p className="text-[#59635C] font-clean mt-2">Edit static text across the website.</p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald text-ivory px-8 py-3 rounded-full font-clean font-bold uppercase tracking-widest hover:bg-emerald/90 transition-all flex items-center gap-2 shadow-lg disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Changes
          </button>
        </div>

        {message && (
          <div className={`p-4 rounded-xl flex items-center gap-3 ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'}`}>
            {message.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            <span className="font-clean font-medium">{message.text}</span>
          </div>
        )}

        <div className="space-y-12">
          {sections.map(section => (
            <div key={section} className="space-y-6">
              <h2 className="text-xl font-serif text-emerald border-b border-accent/10 pb-2">{section} Section</h2>
              <div className="grid grid-cols-1 gap-6">
                {content.filter(c => c.section === section).map(item => (
                  <div key={item.key} className="bg-white p-6 rounded-2xl border border-accent/10 space-y-2 shadow-sm">
                    <label className="text-xs font-bold text-emerald uppercase tracking-widest font-clean">
                      {item.key.replace(/_/g, ' ')}
                    </label>
                    {item.content.length > 100 ? (
                      <textarea
                        value={item.content}
                        onChange={(e) => handleChange(item.key, e.target.value)}
                        className="w-full px-4 py-3 rounded-lg border border-accent/20 focus:border-emerald outline-none font-clean bg-[#F5F1E8]/30 min-h-[120px]"
                      />
                    ) : (
                      <input
                        type="text"
                        value={item.content}
                        onChange={(e) => handleChange(item.key, e.target.value)}
                        className="w-full px-4 py-3 rounded-lg border border-accent/20 focus:border-emerald outline-none font-clean bg-[#F5F1E8]/30"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default WebsiteContent;
